import { computed, ref } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { resolveRulerVariant } from 'dashboard/helper/lais/priceRuler';
import { formatCurrency, numericValue } from '../utils/qprecoFormatters';
import {
  ADMIN_FEE_PERCENTAGE,
  MINIMUM_ADMIN_FEE,
  PRICE_LIMITS,
} from '../utils/qprecoConstants';

/**
 * Faixa de preço estimada, régua de percentis, quebra de valores e edição do
 * preço do imóvel na Área QPreço.
 *
 * Extraído de `pages/AreaQprecoIndex.vue`: mesmos limites por finalidade,
 * mesma taxa de administração e mesmo payload de `ImovelAPI.update`.
 */
export function useQprecoPricing({
  detailInterest,
  estimate,
  isRentFlow,
  onPriceSaved,
}) {
  const editingPrice = ref(false);
  const savingPrice = ref(false);
  const priceInput = ref('');
  const priceError = ref('');

  const priceLimits = computed(
    () =>
      PRICE_LIMITS[detailInterest.value?.finalidade] ||
      (isRentFlow.value ? PRICE_LIMITS.aluguel : PRICE_LIMITS.venda)
  );
  const suggestedPrice = computed(() =>
    numericValue(
      estimate.value?.estimated_price || detailInterest.value?.estimate_value
    )
  );
  const fasterSuggestion = computed(() =>
    numericValue(estimate.value?.price_range_low || estimate.value?.price_p25)
  );
  const maxSuggestion = computed(() =>
    numericValue(estimate.value?.price_range_high || estimate.value?.price_p75)
  );
  const currentPrice = computed(() => {
    const fieldValue = isRentFlow.value
      ? detailInterest.value?.valor_aluguel
      : detailInterest.value?.valor_venda;
    return numericValue(fieldValue) || suggestedPrice.value;
  });
  const rulerPercentiles = computed(() => ({
    p25: numericValue(estimate.value?.price_p25 || fasterSuggestion.value),
    p50: numericValue(
      estimate.value?.median_price ||
        estimate.value?.price_p50 ||
        suggestedPrice.value
    ),
    p75: numericValue(estimate.value?.price_p75 || maxSuggestion.value),
  }));
  const rulerMinPrice = computed(() =>
    numericValue(
      estimate.value?.price_range_low ||
        estimate.value?.estimated_min ||
        estimate.value?.price_p25
    )
  );
  const rulerMaxPrice = computed(() =>
    numericValue(
      estimate.value?.price_range_high ||
        estimate.value?.estimated_max ||
        estimate.value?.price_p75
    )
  );
  const rulerMarkerValue = computed(() =>
    numericValue(
      estimate.value?.main_marker_price ||
        estimate.value?.estimated_price ||
        estimate.value?.median_price ||
        currentPrice.value
    )
  );
  const rulerMarkerColor = computed(
    () => estimate.value?.main_marker_color || 'gray'
  );
  const rulerVariant = computed(() =>
    resolveRulerVariant(detailInterest.value?.finalidade)
  );
  const pricePerArea = computed(() =>
    numericValue(estimate.value?.median_price_per_sqm)
  );
  const priceQuestion = computed(() =>
    isRentFlow.value
      ? 'Por quanto o imóvel será alugado?'
      : 'Por quanto o imóvel será vendido?'
  );
  const confidenceTitle = computed(() =>
    estimate.value?.confidence === 'high'
      ? 'Alta confiança'
      : 'Dados limitados na região'
  );
  const confidenceBody = computed(() =>
    estimate.value?.confidence === 'high'
      ? 'Já negociamos muitos imóveis parecidos no mesmo bairro.'
      : 'Temos poucos dados para estimar o preço deste imóvel. Estamos trabalhando para oferecer essa estimativa em breve.'
  );
  const priceSectionTitle = computed(() => 'Faixa de preço estimada');
  const priceHeading = computed(() =>
    suggestedPrice.value && currentPrice.value === suggestedPrice.value
      ? 'Valor sugerido'
      : 'Valor definido'
  );
  const showPositivePriceAlert = computed(
    () =>
      currentPrice.value &&
      fasterSuggestion.value &&
      maxSuggestion.value &&
      currentPrice.value >= fasterSuggestion.value &&
      currentPrice.value <= maxSuggestion.value
  );
  const showHighPriceAlert = computed(
    () =>
      currentPrice.value &&
      maxSuggestion.value &&
      currentPrice.value > maxSuggestion.value
  );
  const priceRangeLabel = computed(
    () =>
      `${formatCurrency(priceLimits.value.min)} e ${formatCurrency(
        priceLimits.value.max
      )}`
  );
  const rentAmount = computed(() => currentPrice.value || suggestedPrice.value);
  const houseTax = computed(() =>
    numericValue(detailInterest.value?.valor_iptu)
  );
  const adminFeeRaw = computed(
    () => rentAmount.value * (ADMIN_FEE_PERCENTAGE / 100)
  );
  const adminFee = computed(() =>
    Math.max(adminFeeRaw.value || 0, MINIMUM_ADMIN_FEE)
  );
  const ownerReceiveAmount = computed(() =>
    Math.max((rentAmount.value || 0) - adminFee.value, 0)
  );
  const priceRows = computed(() => {
    if (isRentFlow.value) {
      return [
        { label: 'Preço de aluguel', value: rentAmount.value },
        { label: 'Preço do IPTU', value: houseTax.value },
        {
          label:
            adminFeeRaw.value < MINIMUM_ADMIN_FEE
              ? 'Taxa mínima de adm.'
              : `Taxa de adm. (${ADMIN_FEE_PERCENTAGE}%)`,
          value: adminFee.value,
        },
        {
          label: 'Proprietário vai receber',
          value: ownerReceiveAmount.value,
          strong: true,
        },
      ];
    }

    return [
      { label: 'Preço do imóvel', value: currentPrice.value },
      { label: 'Valor sugerido', value: suggestedPrice.value },
      { label: 'Valor máximo', value: maxSuggestion.value },
    ];
  });

  const startEditPrice = () => {
    editingPrice.value = true;
    priceError.value = '';
    priceInput.value = String(
      Math.round(currentPrice.value || suggestedPrice.value || 0)
    );
  };

  const cancelEditPrice = () => {
    editingPrice.value = false;
    priceError.value = '';
  };

  const parsePriceInput = () => {
    const rawInput = String(priceInput.value || '').trim();
    const parsed = Number(rawInput.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const savePrice = async () => {
    if (!detailInterest.value?.imovel_id) {
      useAlert('Vincule um imóvel antes de alterar o valor.');
      return;
    }

    const parsed = parsePriceInput();
    if (!Number.isInteger(parsed)) {
      priceError.value = 'Apenas valores inteiros são permitidos';
      return;
    }

    if (parsed < priceLimits.value.min || parsed > priceLimits.value.max) {
      priceError.value = `O valor deve estar entre ${priceRangeLabel.value}`;
      return;
    }

    savingPrice.value = true;
    try {
      const payload = isRentFlow.value
        ? { valor_aluguel: parsed }
        : { valor_venda: parsed };
      await ImovelAPI.update(detailInterest.value.imovel_id, payload);
      useAlert('Valor atualizado com sucesso.');
      editingPrice.value = false;
      await onPriceSaved();
    } catch (error) {
      useAlert('Não foi possível alterar o valor agora. Tente novamente.');
    } finally {
      savingPrice.value = false;
    }
  };

  return {
    editingPrice,
    savingPrice,
    priceInput,
    priceError,
    priceLimits,
    suggestedPrice,
    fasterSuggestion,
    maxSuggestion,
    currentPrice,
    rulerPercentiles,
    rulerMinPrice,
    rulerMaxPrice,
    rulerMarkerValue,
    rulerMarkerColor,
    rulerVariant,
    pricePerArea,
    priceQuestion,
    confidenceTitle,
    confidenceBody,
    priceSectionTitle,
    priceHeading,
    showPositivePriceAlert,
    showHighPriceAlert,
    priceRangeLabel,
    priceRows,
    startEditPrice,
    cancelEditPrice,
    savePrice,
  };
}
